<template>
  <div class="axdoc" :class="[`axdoc--${mode}`, `axdoc--${layout.page.orientation}`]">
    <section
      v-for="(sheet, sheetIdx) in copySheets"
      :key="`sheet-${sheetIdx}`"
      class="report-page"
      :class="{
        'report-page--landscape': layout.page.orientation === 'landscape',
        'page-break': sheetIdx > 0,
        'axdoc__sheet--half': halfCut,
      }"
    >
      <template v-for="(copy, copyPos) in sheet" :key="`copy-${copy.idx}`">
        <div v-if="halfCut && copyPos > 0" class="axdoc__cutline" aria-hidden="true">
          <span>✂ 절취선</span>
        </div>
        <div class="axdoc__copy" :class="{ 'axdoc__copy--half': halfCut }">
          <!-- 머리말 — 로고 / 제목 / 결재란·발행일시 -->
          <header class="axdoc__head">
            <div class="axdoc__head-left">
              <img
                v-if="layout.page.showCompanyLogo && data.company.logoDataUrl"
                class="axdoc__logo"
                :src="data.company.logoDataUrl"
                alt=""
              />
            </div>
            <div class="axdoc__titles">
              <h1 class="axdoc__title">{{ layout.page.title }}</h1>
              <p v-if="layout.page.subtitle" class="axdoc__subtitle">{{ layout.page.subtitle }}</p>
              <p v-if="copy.label" class="axdoc__copy-label">({{ copy.label }})</p>
            </div>
            <div class="axdoc__head-right">
              <table v-if="layout.page.showApprovalLine" class="axdoc__approval">
                <tbody>
                  <tr>
                    <th>작성</th>
                    <th v-if="hasReviewStage">검토</th>
                    <th>확인</th>
                  </tr>
                  <tr>
                    <td></td>
                    <td v-if="hasReviewStage"></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <p v-if="layout.page.showIssuedAt" class="axdoc__issued">
                발행일시 {{ data.meta.issuedAt }}
              </p>
            </div>
          </header>

          <template v-for="section in layout.sections" :key="section.key">
            <!-- HEADER_FIELDS — 라벨/값 표 -->
            <div v-if="section.type === 'HEADER_FIELDS'" class="axdoc__section report-section">
              <h2 v-if="section.title" class="axdoc__section-title">{{ section.title }}</h2>
              <table class="axdoc__fields">
                <tbody>
                  <tr v-for="(row, ri) in chunk(section.fields, section.columnsPerRow)" :key="ri">
                    <template v-for="field in row" :key="field.key">
                      <th :style="{ width: `${20 / section.columnsPerRow}%` }">
                        {{ field.label
                        }}<span v-if="field.unit" class="axdoc__unit">({{ field.unit }})</span>
                      </th>
                      <td>{{ scalarValue(field) }}</td>
                    </template>
                    <template v-for="n in section.columnsPerRow - row.length" :key="`pad-${n}`">
                      <th></th>
                      <td></td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- PARTY_BOX — 공급자 / 공급받는자 -->
            <div v-else-if="section.type === 'PARTY_BOX'" class="axdoc__section report-section">
              <h2 v-if="section.title" class="axdoc__section-title">{{ section.title }}</h2>
              <div class="axdoc__parties">
                <div v-for="party in section.parties" :key="party.role" class="axdoc__party">
                  <div class="axdoc__party-label">{{ party.label }}</div>
                  <table class="axdoc__party-table">
                    <tbody>
                      <tr v-for="line in partyLines(party)" :key="line.label">
                        <th>{{ line.label }}</th>
                        <td>
                          <span class="axdoc__party-value">{{ line.value }}</span>
                          <img
                            v-if="line.seal && data.company.sealDataUrl"
                            class="axdoc__seal"
                            :src="data.company.sealDataUrl"
                            alt=""
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- TABLE — BOUND(데이터 연결) / FIXED·DYNAMIC(빈 서식) -->
            <div v-else-if="section.type === 'TABLE'" class="axdoc__section report-table-wrap">
              <h2 v-if="section.title" class="axdoc__section-title">{{ section.title }}</h2>
              <table class="axdoc__table report-table">
                <thead>
                  <template v-if="hasGroups(section)">
                    <tr>
                      <th v-if="section.showRowNo" rowspan="2" class="axdoc__col-no">No</th>
                      <th v-if="section.rowMode !== 'BOUND'" rowspan="2">
                        {{ section.rowHeaderLabel ?? '' }}
                      </th>
                      <template v-for="g in groupSpans(section)" :key="g.key">
                        <th v-if="g.group" :colspan="g.span">{{ g.group }}</th>
                        <th v-else rowspan="2" :style="colStyle(g.column)">
                          {{ g.column.label
                          }}<span v-if="g.column.unit" class="axdoc__unit"
                            >({{ g.column.unit }})</span
                          >
                        </th>
                      </template>
                    </tr>
                    <tr>
                      <template v-for="col in section.columns" :key="col.key">
                        <th v-if="col.group" :style="colStyle(col)">
                          {{ col.label
                          }}<span v-if="col.unit" class="axdoc__unit">({{ col.unit }})</span>
                        </th>
                      </template>
                    </tr>
                  </template>
                  <tr v-else>
                    <th v-if="section.showRowNo" class="axdoc__col-no">No</th>
                    <th v-if="section.rowMode !== 'BOUND'">{{ section.rowHeaderLabel ?? '' }}</th>
                    <th v-for="col in section.columns" :key="col.key" :style="colStyle(col)">
                      {{ col.label
                      }}<span v-if="col.unit" class="axdoc__unit">({{ col.unit }})</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in tableRows(section)" :key="ri">
                    <td v-if="section.showRowNo" class="axdoc__col-no">
                      {{ row.blank ? '' : ri + 1 }}
                    </td>
                    <th v-if="section.rowMode !== 'BOUND'" class="axdoc__rowhead">
                      {{ row.label ?? '' }}
                    </th>
                    <td v-for="col in section.columns" :key="col.key" :class="cellClass(col)">
                      {{ row.blank ? '' : cellValue(row.values, col) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="section.showSummary">
                  <tr>
                    <td v-if="section.showRowNo"></td>
                    <th v-if="section.rowMode !== 'BOUND'"></th>
                    <td v-for="(col, ci) in section.columns" :key="col.key" :class="cellClass(col)">
                      <template v-if="col.summary === 'SUM'">{{
                        summaryValue(section, col)
                      }}</template>
                      <template v-else-if="ci === 0">합계</template>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- CHECKLIST — 점검표(입력형 빈 서식) -->
            <div v-else-if="section.type === 'CHECKLIST'" class="axdoc__section report-table-wrap">
              <h2 v-if="section.title" class="axdoc__section-title">{{ section.title }}</h2>
              <table class="axdoc__table report-table">
                <thead>
                  <tr>
                    <th class="axdoc__col-no">No</th>
                    <th>점검 항목</th>
                    <th style="width: 18%">
                      {{ section.resultType === 'CHECK' ? '확인' : '적/부' }}
                    </th>
                    <th style="width: 26%">비고</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, ii) in section.items" :key="item.key">
                    <td class="axdoc__col-no">{{ ii + 1 }}</td>
                    <td>{{ item.label }}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- TEXT_BLOCK -->
            <div v-else-if="section.type === 'TEXT_BLOCK'" class="axdoc__section report-section">
              <h2 class="axdoc__section-title">{{ section.label }}</h2>
              <div
                class="axdoc__text-block report-remarks"
                :style="{ minHeight: `${(section.rows ?? 2) * 22}px` }"
              >
                {{ textBlockValue(section) }}
              </div>
            </div>

            <!-- TOTALS -->
            <div v-else-if="section.type === 'TOTALS'" class="axdoc__section report-section">
              <div class="axdoc__totals" :class="`axdoc__totals--${section.align ?? 'right'}`">
                <table class="axdoc__totals-table">
                  <tbody>
                    <tr
                      v-for="(row, ri) in section.rows"
                      :key="ri"
                      :class="{ 'is-emphasis': row.emphasis }"
                    >
                      <th>{{ row.label }}</th>
                      <td>{{ bindValue(row.bind) }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="section.amountInWords" class="axdoc__amount-words">
                  {{ amountWords(section) }}
                </p>
              </div>
            </div>

            <!-- SIGNATURE -->
            <div v-else-if="section.type === 'SIGNATURE'" class="axdoc__section report-section">
              <div class="axdoc__signatures">
                <div v-for="signer in section.signers" :key="signer" class="axdoc__signature">
                  <span class="axdoc__signature-label">{{ SIGNER_LABELS[signer] }}</span>
                  <span class="axdoc__signature-line"></span>
                  <span class="axdoc__signature-seal">(인)</span>
                </div>
              </div>
            </div>
          </template>

          <footer v-if="showFooter" class="axdoc__foot">
            <span>{{ data.company.name }}</span>
            <span v-if="data.company.phone"> · Tel {{ data.company.phone }}</span>
            <span class="axdoc__foot-right">{{ data.meta.templateName }}</span>
          </footer>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * AxDocumentRenderer — 문서 서식(layout) + 렌더 컨텍스트(data) → 종이 모양 A4 HTML.
 *
 * 설계: docs/designs/core-document-template-engine.md §5.5
 *
 * 값의 출처는 `bind` 유무로 갈린다.
 *  - bind 있음: 컨텍스트에서 읽어 표시(발주서·거래명세서 등 파생 문서)
 *  - bind 없음: 빈 칸으로 인쇄(현장에서 손으로 채우는 서식)
 *
 * 스타일은 전역 클래스(`document-print.css`)를 쓴다 — 인쇄 격리(`html.report-print-isolation`)와
 * html2canvas 기반 PDF 캡처가 scoped 속성 선택자에 의존하지 않도록 하기 위함이다.
 */
import { computed } from 'vue';
import {
  formatDocumentValue,
  resolveContextPath,
  stringifyDocValue,
  sumRowValues,
  toKoreanAmountSentence,
  type DocColumnDef,
  type DocFieldBinding,
  type DocFieldDef,
  type DocPartyDef,
  type DocSectionTable,
  type DocSectionTextBlock,
  type DocSectionTotals,
  type DocTemplateLayout,
  type DocumentRenderContext,
  type DocumentRow,
} from '@axsolutionsdev/shared';

import './document-print.css';

const props = withDefaults(
  defineProps<{
    layout: DocTemplateLayout;
    data: DocumentRenderContext;
    /** view = 화면 미리보기 / print = 인쇄·PDF (여백·그림자 차이) */
    mode?: 'view' | 'print';
    /** 하단 회사명·서식명 푸터 표시 */
    showFooter?: boolean;
    /**
     * 금액 숨김 — 서식은 그대로 두고 MONEY 형식 칸(단가·공급가액·세액·합계)과 한글 금액만 「—」로 찍는다
     * (거래명세서 「금액표시」 끄기, 구포 #96).
     */
    hideMoney?: boolean;
  }>(),
  { mode: 'view', showFooter: true, hideMoney: false },
);

/** 금액 숨김일 때 MONEY 칸 대신 찍는 표시 */
const HIDDEN_MONEY = '—';

/** MONEY 바인딩은 금액 숨김이면 값과 무관하게 「—」 */
function formatBound(value: unknown, bind: DocFieldBinding): string {
  if (props.hideMoney && bind.format === 'MONEY') return HIDDEN_MONEY;
  return formatDocumentValue(value, bind);
}

const SIGNER_LABELS: Record<string, string> = {
  WRITER: '작성자',
  REVIEWER: '검토자',
  CONFIRMER: '확인자',
};

/** 공급자/공급받는자 박스에 찍는 표준 항목 순서. */
const PARTY_LINES: Array<{ key: string; label: string }> = [
  { key: 'name', label: '상호' },
  { key: 'bizNo', label: '사업자등록번호' },
  { key: 'ceo', label: '대표자' },
  { key: 'address', label: '주소' },
  { key: 'bizType', label: '업태' },
  { key: 'bizItem', label: '종목' },
  { key: 'phone', label: '전화' },
];

const hasReviewStage = computed(() => props.layout.page.approvalStages === 3);

/** copyLabels 가 있으면 그 수만큼 반복 인쇄, 없으면 1벌(라벨 없음). */
const copies = computed<Array<string | null>>(() => {
  const labels = props.layout.page.copyLabels;
  return labels && labels.length > 0 ? [...labels] : [null];
});

/** HALF_CUT: 사본 2벌을 한 장에 상·하 절취식으로 배치 — 사본이 2벌 미만이면 무시. */
const halfCut = computed<boolean>(
  () => props.layout.page.copySheet === 'HALF_CUT' && copies.value.length > 1,
);

interface CopyEntry {
  label: string | null;
  idx: number;
}

/** 인쇄 장 단위 묶음 — 기본은 1벌=1장, HALF_CUT 은 2벌=1장. */
const copySheets = computed<CopyEntry[][]>(() => {
  const entries = copies.value.map((label, idx) => ({ label, idx }));
  return halfCut.value ? chunk(entries, 2) : entries.map((entry) => [entry]);
});

function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** 컨텍스트 절대 경로 바인딩 값. */
function bindValue(bind?: DocFieldBinding): string {
  if (!bind) return '';
  return formatBound(resolveContextPath(props.data, bind.path), bind);
}

function scalarValue(field: DocFieldDef): string {
  return bindValue(field.bind);
}

/** BOUND 표의 셀 — 경로는 행 기준 상대. */
function cellValue(row: DocumentRow | undefined, col: DocColumnDef): string {
  if (!col.bind || !row) return '';
  return formatBound(resolveContextPath(row, col.bind.path), col.bind);
}

function textBlockValue(section: DocSectionTextBlock): string {
  return bindValue(section.bind);
}

function amountWords(section: DocSectionTotals): string {
  if (!section.amountInWords) return '';
  if (props.hideMoney) return HIDDEN_MONEY;
  const raw = resolveContextPath(props.data, section.amountInWords.path);
  // 공급자가 이미 문장으로 넘겼으면 그대로, 숫자면 한글 금액으로 변환한다.
  if (typeof raw === 'string' && raw.trim() !== '' && Number.isNaN(Number(raw))) return raw;
  return toKoreanAmountSentence(raw);
}

interface RenderRow {
  values?: DocumentRow;
  label?: string;
  /** 종이 양식 채움용 빈 행 */
  blank?: boolean;
}

/**
 * 표의 행 목록.
 *  - BOUND: `data.tables[dataKey]` + minRows 만큼 빈 행 채움
 *  - FIXED: 고정 행 라벨만(값은 손으로 기입)
 *  - DYNAMIC: maxRows 상한 대신 10행의 빈 서식(인쇄용)
 */
function tableRows(section: DocSectionTable): RenderRow[] {
  if (section.rowMode === 'BOUND') {
    const rows = section.dataKey ? (props.data.tables?.[section.dataKey] ?? []) : [];
    const out: RenderRow[] = rows.map((values) => ({ values }));
    const min = section.minRows ?? 0;
    for (let i = out.length; i < min; i += 1) out.push({ blank: true });
    return out;
  }
  if (section.rowMode === 'FIXED') {
    return (section.fixedRows ?? []).map((r) => ({ label: r.label, blank: true }));
  }
  const count = Math.min(section.maxRows ?? 10, 20);
  return Array.from({ length: count }, () => ({ blank: true }));
}

function summaryValue(section: DocSectionTable, col: DocColumnDef): string {
  if (section.rowMode !== 'BOUND' || !section.dataKey || !col.bind) return '';
  const rows = props.data.tables?.[section.dataKey] ?? [];
  const total = sumRowValues(rows, col.bind.path);
  return formatBound(total, col.bind);
}

function hasGroups(section: DocSectionTable): boolean {
  return section.columns.some((c) => !!c.group);
}

/** 인접한 동일 group 컬럼을 병합 헤더로 묶는다. */
function groupSpans(
  section: DocSectionTable,
): Array<{ key: string; group?: string; span: number; column: DocColumnDef }> {
  const out: Array<{ key: string; group?: string; span: number; column: DocColumnDef }> = [];
  for (const col of section.columns) {
    const last = out[out.length - 1];
    if (col.group && last?.group === col.group) {
      last.span += 1;
      continue;
    }
    out.push({
      key: col.key,
      ...(col.group ? { group: col.group } : {}),
      span: 1,
      column: col,
    });
  }
  return out;
}

function colStyle(col: DocColumnDef): Record<string, string> {
  return col.width ? { width: `${col.width}mm` } : {};
}

function cellClass(col: DocColumnDef): string[] {
  const align =
    col.align ??
    (col.bind && ['MONEY', 'QTY', 'NUMBER', 'PERCENT'].includes(col.bind.format ?? '')
      ? 'right'
      : undefined);
  return align ? [`axdoc__cell--${align}`] : [];
}

interface PartyLine {
  label: string;
  value: string;
  seal?: boolean;
}

/** 회사(COMPANY) 또는 컨텍스트(CONTEXT) 에서 당사자 정보를 뽑는다. 값이 없는 줄은 숨긴다. */
function partyLines(party: DocPartyDef): PartyLine[] {
  const read = (key: string): string => {
    const path = party.source === 'COMPANY' ? `company.${key}` : `${party.pathPrefix}.${key}`;
    return stringifyDocValue(resolveContextPath(props.data, path));
  };
  const lines: PartyLine[] = [];
  for (const def of PARTY_LINES) {
    const value = read(def.key);
    if (!value) continue;
    lines.push({
      label: def.label,
      value,
      ...(def.key === 'name' && party.showSeal ? { seal: true } : {}),
    });
  }
  // 전부 비어도 박스 골격은 남긴다(종이 대조 시 항목 위치가 보여야 한다).
  return lines.length > 0
    ? lines
    : PARTY_LINES.slice(0, 3).map((d) => ({ label: d.label, value: '' }));
}
</script>
